/**
 * JourneyStepper — navegação hierárquica da jornada.
 *
 * Extraído do protótipo: 2 linhas
 *   1) Chips de fase (6 fases)
 *   2) Etapas da fase ativa
 *
 * Clique livre para trás até `furthest`; avanço bloqueado.
 * Acessibilidade: role="navigation", botões com aria-current, aria-disabled.
 */
import { Check } from 'lucide-react';
import { C } from '@/theme/palette';
import { PHASES, STEP_META } from './JourneyContext';
import { useJourney } from './JourneyContext';

// ─── Fase chip ────────────────────────────────────────────────────────────────

function PhaseChip({ phase, status }) {
  const colorMap = {
    done:         { bg: `${C.cyber}22`, border: C.cyberDim, text: C.cyber },
    current:      { bg: `${C.cyber}33`, border: C.cyber,    text: C.cyber },
    'gate-pending': { bg: `${C.digital}22`, border: C.digital, text: C.digital },
    future:       { bg: 'transparent',  border: C.blackBorder, text: C.neutralMuted },
  };
  const style = colorMap[status] ?? colorMap.future;

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        padding: '4px 12px',
        borderRadius: 100,
        backgroundColor: style.bg,
        border: `1px solid ${style.border}`,
        fontSize: 11,
        fontWeight: 600,
        color: style.text,
        letterSpacing: '0.03em',
        whiteSpace: 'nowrap',
      }}
    >
      {status === 'done' && <Check size={10} aria-hidden="true" />}
      {phase.label}
    </div>
  );
}

// ─── Step dot ─────────────────────────────────────────────────────────────────

function StepDot({ step, isCurrent, isReached, onClick, label }) {
  const canClick = isReached;
  const color = isCurrent ? C.cyber : isReached ? C.cyberDim : C.neutralMuted;

  return (
    <button
      onClick={canClick ? onClick : undefined}
      aria-current={isCurrent ? 'step' : undefined}
      aria-disabled={!canClick}
      disabled={!canClick}
      title={label}
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 6,
        background: 'transparent',
        border: 'none',
        cursor: canClick ? 'pointer' : 'not-allowed',
        padding: 0,
        opacity: canClick ? 1 : 0.45,
      }}
    >
      <div
        style={{
          width: isCurrent ? 10 : 8,
          height: isCurrent ? 10 : 8,
          borderRadius: '50%',
          backgroundColor: color,
          boxShadow: isCurrent ? `0 0 0 3px ${C.cyber}33` : 'none',
          transition: 'all 200ms',
        }}
        aria-hidden="true"
      />
      <span
        style={{
          fontSize: 10,
          fontWeight: isCurrent ? 600 : 400,
          color: isCurrent ? C.cyber : C.neutralMuted,
          letterSpacing: '0.03em',
          maxWidth: 80,
          textAlign: 'center',
          lineHeight: 1.3,
        }}
      >
        {label}
      </span>
    </button>
  );
}

// ─── JourneyStepper ───────────────────────────────────────────────────────────

export function JourneyStepper() {
  const { current, steps, furthest, projectType, goToStep } = useJourney();

  // Fases que têm pelo menos uma etapa ativa no wizard atual
  const activePhaseId = STEP_META[current]?.phase ?? 'definicoes';

  // Status de cada fase
  function phaseStatus(phase) {
    const phaseSteps = steps.filter((s) => STEP_META[s]?.phase === phase.id);
    if (phaseSteps.length === 0) return 'future';

    const allDone = phaseSteps.every((s) => {
      const idx  = steps.indexOf(s);
      const fIdx = steps.indexOf(furthest);
      return idx < fIdx;
    });
    if (allDone) return 'done';

    const hasCurrent = phaseSteps.includes(current);
    if (hasCurrent) return 'current';

    // Fases cujos steps ainda não foram alcançados
    const firstIdx = steps.indexOf(phaseSteps[0]);
    const fIdx     = steps.indexOf(furthest);
    if (firstIdx > fIdx) return 'future';

    return 'current';
  }

  // Etapas da fase ativa para mostrar na linha 2
  const activeSteps = steps.filter((s) => STEP_META[s]?.phase === activePhaseId);

  return (
    <nav aria-label="Progresso da jornada" style={{ marginBottom: 32 }}>
      {/* Linha 1 — Fases */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          marginBottom: 20,
          flexWrap: 'wrap',
        }}
      >
        {PHASES.map((phase, i) => (
          <div key={phase.id} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <PhaseChip phase={phase} status={phaseStatus(phase)} />
            {i < PHASES.length - 1 && (
              <div
                aria-hidden="true"
                style={{
                  width: 20,
                  height: 1,
                  backgroundColor: C.blackBorder,
                }}
              />
            )}
          </div>
        ))}
      </div>

      {/* Linha 2 — Etapas da fase ativa */}
      {activeSteps.length > 0 && (
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 0,
            padding: '12px 20px',
            backgroundColor: C.blackElev2,
            borderRadius: 8,
            border: `1px solid ${C.blackBorder}`,
          }}
        >
          {activeSteps.map((step, i) => {
            const idx  = steps.indexOf(step);
            const fIdx = steps.indexOf(furthest);
            const isReached = idx <= fIdx;
            const isCurrent = step === current;

            return (
              <div key={step} style={{ display: 'flex', alignItems: 'flex-start', flex: 1 }}>
                <StepDot
                  step={step}
                  isCurrent={isCurrent}
                  isReached={isReached}
                  onClick={() => goToStep(step)}
                  label={STEP_META[step]?.label ?? step}
                />
                {i < activeSteps.length - 1 && (
                  <div
                    aria-hidden="true"
                    style={{
                      flex: 1,
                      height: 1,
                      marginTop: 4,
                      backgroundColor: isReached ? C.cyberDim : C.blackBorder,
                    }}
                  />
                )}
              </div>
            );
          })}
        </div>
      )}
    </nav>
  );
}
